import { DUMMY_PLAYER_COUNT } from '../../helpers/StatisticsOpponentHelper';
import { EventLightVm, MatchDetailVm, MatchPlayerVm, MatchVm } from '../../utils/api';
import { playerDisplayName } from './insightsMatchStats';
import { loadMatchBundles, MatchBundle } from './insightsQueries';

/*
 * Season box score for the players of one club, built from the light events of a bounded
 * number of recent matches. Opponent placeholders are never counted: analytics keeps their
 * events (goals against us, locations) but not their careers.
 */

export interface PlayerSeasonRow {
    playerId: number;
    name: string;
    capNumbers: number[];
    matches: number;
    goals: number;
    shots: number;
    assists: number;
    exclusionsDrawn: number;
    exclusionsCommitted: number;
    turnovers: number;
    steals: number;
    blocks: number;
    saves: number;
}

export interface PlayerMatchLine {
    matchId: number;
    startDate?: Date;
    opponentName: string;
    isHome: boolean;
    goals: number;
    shots: number;
    assists: number;
    exclusionsDrawn: number;
    turnovers: number;
    saves: number;
}

export interface PlayerSeasonResult {
    rows: PlayerSeasonRow[];
    /** Matches actually aggregated, so the UI can state the sample size. */
    matchesAnalysed: number;
    linesByPlayerId: Map<number, PlayerMatchLine[]>;
}

const PLACEHOLDER_NAME = /^opp\s*\d+$/i;

/**
 * Statistics-mode opponents are generated as "Opp 1".."Opp 16" by
 * StatisticsOpponentHelper. They exist so a match can be logged, not to be ranked.
 */
export const isPlaceholderPlayerName = (name: string): boolean => {
    const trimmed = (name || '').trim();
    if (!PLACEHOLDER_NAME.test(trimmed)) return false;
    const capNumber = parseInt(trimmed.replace(/\D+/g, ''), 10);
    return capNumber >= 1 && capNumber <= DUMMY_PLAYER_COUNT;
};

const emptyRow = (playerId: number, name: string): PlayerSeasonRow => ({
    playerId,
    name,
    capNumbers: [],
    matches: 0,
    goals: 0,
    shots: 0,
    assists: 0,
    exclusionsDrawn: 0,
    exclusionsCommitted: 0,
    turnovers: 0,
    steals: 0,
    blocks: 0,
    saves: 0,
});

const emptyLine = (match: MatchDetailVm, isHome: boolean): PlayerMatchLine => ({
    matchId: match.id,
    startDate: match.startDate,
    opponentName:
        (isHome ? match.awayTeamDisplayName : match.homeTeamDisplayName) || 'Unknown opponent',
    isHome,
    goals: 0,
    shots: 0,
    assists: 0,
    exclusionsDrawn: 0,
    turnovers: 0,
    saves: 0,
});

export const shootingPercentText = (goals: number, shots: number): string =>
    shots > 0 ? `${Math.round((goals / shots) * 100)}%` : '-';

export interface FormComparison {
    label: string;
    recent: number;
    season: number;
    /** Positive means the recent run is above the season average. */
    delta: number;
    /** Turnovers going down is good, goals going down is not. */
    lowerIsBetter?: boolean;
}

const average = (values: number[]): number =>
    values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;

/**
 * Recent form against the whole sample. Lines must be newest first; a short sample simply
 * reports the same number on both sides.
 */
export const buildFormComparison = (
    lines: PlayerMatchLine[],
    recentCount = 5
): FormComparison[] => {
    const recent = lines.slice(0, recentCount);
    const metric = (
        label: string,
        pick: (line: PlayerMatchLine) => number,
        lowerIsBetter?: boolean
    ): FormComparison => {
        const recentValue = average(recent.map(pick));
        const seasonValue = average(lines.map(pick));
        return {
            label,
            recent: recentValue,
            season: seasonValue,
            delta: recentValue - seasonValue,
            lowerIsBetter,
        };
    };

    return [
        metric('Goals', (line) => line.goals),
        metric('Shots', (line) => line.shots),
        metric('Assists', (line) => line.assists),
        metric('Exclusions drawn', (line) => line.exclusionsDrawn),
        metric('Turnovers', (line) => line.turnovers, true),
    ];
};

/** GK form: last N vs filtered average (saves-focused). */
export const buildGkFormComparison = (
    lines: PlayerMatchLine[],
    recentCount = 5
): FormComparison[] => {
    const recent = lines.slice(0, recentCount);
    const metric = (
        label: string,
        pick: (line: PlayerMatchLine) => number,
        lowerIsBetter?: boolean
    ): FormComparison => {
        const recentValue = average(recent.map(pick));
        const seasonValue = average(lines.map(pick));
        return {
            label,
            recent: recentValue,
            season: seasonValue,
            delta: recentValue - seasonValue,
            lowerIsBetter,
        };
    };

    return [metric('Saves', (line) => line.saves)];
};

export const accumulateMatch = (
    bundle: MatchBundle,
    teamIds: number[],
    rowsByPlayerId: Map<number, PlayerSeasonRow>,
    linesByPlayerId: Map<number, PlayerMatchLine[]>
): void => {
    const { detail, events } = bundle;
    const isHome = teamIds.includes(detail.homeTeamId);

    // matchPlayerId -> our player, so opponent ids simply fall through as undefined.
    const ours = new Map<number, MatchPlayerVm>();
    (detail.players || []).forEach((matchPlayer: MatchPlayerVm) => {
        if (matchPlayer.id == null) return;
        if (!teamIds.includes(matchPlayer.teamId)) return;
        if (isPlaceholderPlayerName(playerDisplayName(matchPlayer))) return;
        ours.set(matchPlayer.id, matchPlayer);
    });

    const lineFor = (matchPlayerId?: number): PlayerMatchLine | undefined => {
        if (matchPlayerId == null) return undefined;
        const matchPlayer = ours.get(matchPlayerId);
        if (!matchPlayer) return undefined;

        const playerId = matchPlayer.playerId;
        const lines = linesByPlayerId.get(playerId) || [];
        let line = lines.find((entry: PlayerMatchLine) => entry.matchId === detail.id);
        if (!line) {
            line = emptyLine(detail, isHome);
            lines.push(line);
            linesByPlayerId.set(playerId, lines);
        }
        return line;
    };

    const rowFor = (matchPlayerId?: number): PlayerSeasonRow | undefined => {
        if (matchPlayerId == null) return undefined;
        const matchPlayer = ours.get(matchPlayerId);
        if (!matchPlayer) return undefined;

        const playerId = matchPlayer.playerId;
        let row = rowsByPlayerId.get(playerId);
        if (!row) {
            row = emptyRow(playerId, playerDisplayName(matchPlayer));
            rowsByPlayerId.set(playerId, row);
        }
        if (matchPlayer.capNumber != null && !row.capNumbers.includes(matchPlayer.capNumber)) {
            row.capNumbers.push(matchPlayer.capNumber);
        }
        return row;
    };

    // Appearances come from the lineup, not from events: a player with no events still played.
    ours.forEach((matchPlayer: MatchPlayerVm) => {
        const row = rowFor(matchPlayer.id);
        if (row) row.matches += 1;
        lineFor(matchPlayer.id);
    });

    events.forEach((event: EventLightVm) => {
        if (event.shot) {
            const shooter = rowFor(event.shot.takenById);
            if (shooter) {
                shooter.shots += 1;
                if (event.shot.isGoal) shooter.goals += 1;
            }
            const shooterLine = lineFor(event.shot.takenById);
            if (shooterLine) {
                shooterLine.shots += 1;
                if (event.shot.isGoal) shooterLine.goals += 1;
            }

            if (event.shot.isGoal) {
                const assist = rowFor(event.shot.assistedById);
                if (assist) assist.assists += 1;
                const assistLine = lineFor(event.shot.assistedById);
                if (assistLine) assistLine.assists += 1;
            }

            const blocker = rowFor(event.shot.blockedById);
            if (blocker) blocker.blocks += 1;

            const keeper = rowFor(event.shot.savedById);
            if (keeper) keeper.saves += 1;
            const keeperLine = lineFor(event.shot.savedById);
            if (keeperLine) keeperLine.saves += 1;
        }

        if (event.exclusion) {
            const excluded = rowFor(event.exclusion.excludedPlayerId);
            if (excluded) excluded.exclusionsCommitted += 1;

            const fouled = rowFor(event.exclusion.fouledPlayerId);
            if (fouled) fouled.exclusionsDrawn += 1;
            const fouledLine = lineFor(event.exclusion.fouledPlayerId);
            if (fouledLine) fouledLine.exclusionsDrawn += 1;
        }

        if (event.turnover) {
            const lost = rowFor(event.turnover.lostPossesionPlayerId);
            if (lost) lost.turnovers += 1;
            const lostLine = lineFor(event.turnover.lostPossesionPlayerId);
            if (lostLine) lostLine.turnovers += 1;

            const won = rowFor(event.turnover.wonPossesionPlayerId);
            if (won) won.steals += 1;
        }
    });
};

export const loadPlayerSeason = async (
    matches: MatchVm[],
    teamIds: number[]
): Promise<PlayerSeasonResult> => {
    const bundles = await loadMatchBundles(matches);
    return seasonFromBundles(bundles, teamIds);
};

/** Aggregate an already-loaded bundle list (for filter recompute without refetch). */
export const seasonFromBundles = (
    bundles: MatchBundle[],
    teamIds: number[]
): PlayerSeasonResult => {
    const rowsByPlayerId = new Map<number, PlayerSeasonRow>();
    const linesByPlayerId = new Map<number, PlayerMatchLine[]>();

    bundles.forEach((bundle: MatchBundle) =>
        accumulateMatch(bundle, teamIds, rowsByPlayerId, linesByPlayerId)
    );

    const rows = Array.from(rowsByPlayerId.values()).sort(
        (a: PlayerSeasonRow, b: PlayerSeasonRow) =>
            b.goals - a.goals || b.assists - a.assists || a.name.localeCompare(b.name)
    );

    return { rows, matchesAnalysed: bundles.length, linesByPlayerId };
};
